/**
 * The audit trail.
 *
 * ═══════════════════════════════════════════════════════════════════
 * APPEND ONLY, AND ENFORCED AT THE DATABASE.
 *
 * An audit event cannot be updated or deleted — a trigger refuses it, so even
 * somebody with direct SQL access cannot quietly remove the record of what they
 * did. Events are sequentially numbered, so a gap is visible.
 *
 * The trail records REFUSALS as well as actions. A blocked budget check, a
 * refused self-approval, a rejected bank-detail verification: the evidence that
 * a control operated is the evidence that it works, and it is worth more at
 * audit than the record of everything that went through smoothly.
 * ═══════════════════════════════════════════════════════════════════
 *
 * The filters live in the address bar rather than in component state. That is
 * what lets a headline figure be a link — "show me the events flagged for
 * attention" is a destination, not a button — and it means the auditor can send
 * a colleague the exact view they are looking at.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, lower } from '../lib/tenant';
import { addDaysIso, dateTime, number, relativeTime, statusLabel, todayIso, type BadgeTone } from '../lib/format';
import { Alert, AsyncPanel, Badge, Card, DataTable, ErrorNotice, PageHeader, Stat } from '../components/ui';

interface AuditEvent {
  id: string;
  sequence_no: number;
  occurred_at: string;
  event_type: string;
  module: string;
  severity: string;
  summary: string;
  actor_label: string;
  actor_name: string | null;
  actor_username: string | null;
  on_behalf_of_name: string | null;
  subject_table: string | null;
  subject_id: string | null;
  subject_reference: string | null;
  entity_name: string | null;
  ip_address: string | null;
}

/**
 * The filter vocabulary as the server sends it. Event types and modules arrive
 * as {value, count} objects, not as bare strings — rendering one of those
 * objects straight into an <option> takes the whole screen down with it.
 */
interface Filters {
  eventTypes: Array<{ value: string; count: number }>;
  modules: Array<{ value: string; count: number }>;
  actors: Array<{ id: string; name: string; count: number }>;
  severities: string[];
  entities: Array<{ entityId: string; entityCode: string; entityName: string }>;
}

interface EventsResponse {
  events: AuditEvent[];
  totalCount: number;
  limit: number;
  offset: number;
}

/** The most the server will return in one request. */
const EVENT_LIMIT = 500;

/** The window the CSV extract covers unless the reader widens it. */
const DEFAULT_WINDOW_DAYS = 30;

/**
 * Severities that mean "nothing to look at here". Everything else — a warning,
 * a security event, a refusal, and anything the server learns to record later —
 * is treated as worth a second look, because the safe default for a severity
 * this screen has not met before is to show it rather than bury it.
 */
const ROUTINE_SEVERITIES = new Set(['info', 'notice']);

const isFlagged = (event: AuditEvent) => !ROUTINE_SEVERITIES.has(event.severity);

/**
 * Severity has its own vocabulary. `statusTone` maps document statuses, and it
 * has no opinion about 'info' or 'security', so routing severity through it
 * painted every badge the same neutral grey.
 */
const SEVERITY_TONES: Record<string, BadgeTone> = {
  info: 'neutral',
  notice: 'info',
  warning: 'warn',
  security: 'brand',
  critical: 'error',
};

/**
 * Where a reference in the About column leads, and what a reader must be
 * allowed to see before the reference becomes a link at all. A link that lands
 * on a permission refusal is worse than plain text, so a reader without the
 * permission gets the reference as text.
 *
 * Subject tables with no detail route of their own — a user account, an anomaly
 * alert, a goods receipt — are deliberately absent.
 */
const RECORD_ROUTES: Record<string, { path: string; permissions: string[] }> = {
  purchase_requisition: { path: '/requisitions', permissions: ['requisition.create', 'requisition.view.all'] },
  purchase_order: { path: '/orders', permissions: ['po.view.all', 'requisition.create'] },
  purchase_invoice: { path: '/invoices', permissions: ['ap.view'] },
  payment_batch: { path: '/payments', permissions: ['ap.view', 'ap.payment.prepare', 'ap.payment.approve'] },
  journal: { path: '/ledger/journals', permissions: ['gl.view'] },
};

/** Cut a long sentence at a word boundary, and only then admit it was cut. */
function shorten(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function AuditPage() {
  const t = useTerms();
  const { can, canAny } = useSession();
  const [params, setParams] = useSearchParams();

  const defaultTo = todayIso();
  const defaultFrom = addDaysIso(defaultTo, -DEFAULT_WINDOW_DAYS);

  const module = params.get('module') ?? '';
  const severity = params.get('severity') ?? '';
  const search = params.get('search') ?? '';
  const actorUserId = params.get('actorUserId') ?? '';
  const from = params.get('from') ?? defaultFrom;
  const to = params.get('to') ?? defaultTo;

  // 'flagged' is not one of the server's severities: it stands for "anything
  // above routine", which is more than one severity and so cannot be asked of
  // the server in a single parameter. It is applied to the loaded events here.
  const flaggedOnly = severity === 'flagged';
  const isFiltered = params.toString() !== '';

  const setFilter = (key: string, value: string) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  /*
   * A business noun for the record an event was about.
   *
   * `subject_table` is the name of a database table. "App user" on eighty rows
   * of the trail reads as a schema dump, so the tables a reader will actually
   * meet are named the way the rest of the product names them, in the tenant's
   * own words wherever the tenant has supplied one.
   */
  const subjectLabels: Record<string, string> = {
    app_user: 'User account',
    purchase_requisition: t.requisition,
    purchase_order: t.purchaseOrder,
    purchase_invoice: `${t.supplier} invoice`,
    goods_receipt: 'Goods receipt',
    payment_batch: 'Payment run',
    journal: 'Journal',
    anomaly_alert: 'Anomaly alert',
    report: 'Report',
  };

  const filters = useAsync(() => api.get<Filters>('/operations/audit/filters'), []);

  const events = useAsync(() => {
    const query = new URLSearchParams({ limit: String(EVENT_LIMIT), from });
    // The server compares `to` against a timestamp, so a bare calendar date
    // would land on midnight and cut the whole of that day out of the results.
    query.set('to', `${to}T23:59:59.999Z`);
    if (module) query.set('module', module);
    if (severity && !flaggedOnly) query.set('severity', severity);
    if (search) query.set('search', search);
    if (actorUserId) query.set('actorUserId', actorUserId);
    return api.get<EventsResponse>(`/operations/audit/events?${query}`);
  }, [module, severity, search, actorUserId, from, to]);

  /*
   * The extract is produced by the server, which numbers it, stamps it with who
   * asked for it, and records the export in the trail. It takes the dates but
   * not the module, severity or search — the footer says so rather than letting
   * the reader assume the CSV matches the screen.
   */
  const exportTrail = useMutation(() =>
    api.download(`/operations/audit/export?${new URLSearchParams({ from, to })}`),
  );

  /*
   * If the lookup has not arrived — or has failed — the option the reader
   * arrived with is still offered, so a filter carried in from a link does not
   * show as a blank select while it is quietly being applied.
   */
  const moduleOptions = filters.data?.modules ?? (module ? [{ value: module, count: 0 }] : []);
  const severityOptions = filters.data?.severities ?? (severity && !flaggedOnly ? [severity] : []);
  const actorOptions = filters.data?.actors ?? (actorUserId ? [{ id: actorUserId, name: 'Selected person', count: 0 }] : []);

  /*
   * Where the "flagged for attention" figure leads, carrying the rest of the
   * filters with it. Undefined once the reader is already there, because a tile
   * that links to the page you are looking at is a control that does nothing.
   */
  const flaggedDrill = flaggedOnly
    ? undefined
    : (() => {
        const next = new URLSearchParams(params);
        next.set('severity', 'flagged');
        return `/audit?${next}`;
      })();

  return (
    <>
      <PageHeader
        title="Audit trail"
        subtitle={`Every recorded event in this ${lower(t.entity)}, in sequence. Append only — the database refuses an update or a delete.`}
      />

      <AsyncPanel state={events}>
        {(data) => {
          const rows = flaggedOnly ? data.events.filter(isFlagged) : data.events;
          const flaggedCount = data.events.filter(isFlagged).length;
          // The server returns newest first, so the first row on screen is the
          // most recent thing the reader is actually being shown.
          const latest = rows[0];
          const truncated = data.totalCount > data.events.length;

          return (
            <div className="stack">
              <div className="grid grid--3">
                <Stat
                  label="Events shown"
                  value={number(rows.length)}
                  detail={
                    truncated
                      ? `The most recent of ${number(data.totalCount)} matching events`
                      : 'Everything matching the filters on screen'
                  }
                />
                <Stat
                  label="Flagged for attention"
                  value={number(flaggedCount)}
                  detail="Warnings, security events and refused actions"
                  to={flaggedDrill}
                  drillLabel="Show these events"
                />
                <Stat
                  label="Most recent"
                  value={latest ? relativeTime(latest.occurred_at) : '—'}
                  detail={latest ? shorten(latest.summary, 64) : 'Nothing in the dates chosen'}
                />
              </div>

              {truncated ? (
                <Alert tone="info" title={`Showing the most recent ${number(data.limit)} events`}>
                  <p>
                    {number(data.totalCount)} events match these filters. Narrow the dates, or filter by module or
                    severity, to bring the earlier ones into view.
                  </p>
                </Alert>
              ) : null}

              <ErrorNotice error={exportTrail.error} onRetry={() => void exportTrail.run()} />

              {filters.error ? (
                <Alert tone="warn" title="The filter lists could not be loaded">
                  <p>
                    Module, severity and person offer fewer choices than usual. The events below are unaffected — the
                    dates and the search box still work.
                  </p>
                </Alert>
              ) : null}

              <Card
                title="Events"
                actions={
                  /* Only somebody the server would actually let export sees the button. */
                  can('audit.export') ? (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={exportTrail.busy}
                      onClick={() => void exportTrail.run()}
                    >
                      {exportTrail.busy ? 'Preparing the extract…' : 'Export (CSV)'}
                    </button>
                  ) : null
                }
                flush
                footer={
                  <div className="stack stack--sm">
                    <p className="smaller muted">
                      Events are numbered in sequence, so a missing number is visible, and nothing here can be edited or
                      removed — the database refuses an update or a delete. Refusals are written to the same sequence as
                      successful actions, so the evidence that a control operated is kept whether or not anything was
                      allowed through.
                    </p>
                    {can('audit.export') ? (
                      <p className="smaller muted">
                        The CSV extract covers every event between the dates above, for every module and severity, and
                        taking it is itself recorded in the trail. The module, severity and search boxes narrow this
                        screen only.
                      </p>
                    ) : null}
                  </div>
                }
              >
                {/* The filter band sits with the table it narrows, not in the card's title bar. */}
                <div className="table-controls" style={{ flexWrap: 'wrap' }}>
                  <label>
                    <span className="visually-hidden">Search the audit trail</span>
                    <input
                      type="search"
                      className="table-search"
                      /* Wide enough that a supplier name or a document number stays readable as the band wraps. */
                      style={{ minWidth: '13rem' }}
                      placeholder="Search summaries and references"
                      value={search}
                      onChange={(e) => setFilter('search', e.currentTarget.value)}
                    />
                  </label>
                  <label className="row" style={{ gap: '0.4rem' }}>
                    <span className="smaller muted">From</span>
                    <input type="date" value={from} max={to} onChange={(e) => setFilter('from', e.currentTarget.value)} />
                  </label>
                  <label className="row" style={{ gap: '0.4rem' }}>
                    <span className="smaller muted">To</span>
                    <input type="date" value={to} min={from} onChange={(e) => setFilter('to', e.currentTarget.value)} />
                  </label>
                  <label className="row" style={{ gap: '0.4rem' }}>
                    <span className="smaller muted">Module</span>
                    <select value={module} onChange={(e) => setFilter('module', e.currentTarget.value)}>
                      <option value="">All</option>
                      {moduleOptions.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.value}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="row" style={{ gap: '0.4rem' }}>
                    <span className="smaller muted">Person</span>
                    <select value={actorUserId} onChange={(e) => setFilter('actorUserId', e.currentTarget.value)}>
                      <option value="">Anyone</option>
                      {actorOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="row" style={{ gap: '0.4rem' }}>
                    <span className="smaller muted">Severity</span>
                    <select value={severity} onChange={(e) => setFilter('severity', e.currentTarget.value)}>
                      <option value="">All</option>
                      <option value="flagged">Flagged for attention</option>
                      {severityOptions.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </select>
                  </label>
                  {isFiltered ? (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    >
                      Clear filters
                    </button>
                  ) : null}
                </div>

                <DataTable
                  caption="Audit events"
                  rows={rows}
                  rowKey={(e) => e.id}
                  pageSize={50}
                  emptyMessage={
                    flaggedOnly
                      ? 'No warnings, security events or refusals in the dates chosen.'
                      : 'No events match these filters.'
                  }
                  columns={[
                    {
                      key: 'seq',
                      header: '#',
                      width: '4.5rem',
                      render: (e) => <span className="mono smaller">{e.sequence_no}</span>,
                      sortValue: (e) => Number(e.sequence_no),
                    },
                    {
                      key: 'when',
                      header: 'When',
                      width: '10rem',
                      render: (e) => (
                        <>
                          <div className="smaller">{dateTime(e.occurred_at)}</div>
                          <div className="smaller muted">{relativeTime(e.occurred_at)}</div>
                        </>
                      ),
                      sortValue: (e) => e.occurred_at,
                    },
                    {
                      key: 'what',
                      header: 'What happened',
                      /*
                       * The machine event code rides on the element rather than
                       * printing under every row. The summary above it is
                       * already a complete sentence, and two hundred lines of
                       * dotted snake case down the middle of the screen tell a
                       * reader nothing they can use. It stays in the CSV
                       * extract, where an auditor can match on it.
                       */
                      render: (e) => <div title={e.event_type}>{e.summary}</div>,
                      sortValue: (e) => e.summary,
                    },
                    {
                      key: 'who',
                      header: 'Who',
                      render: (e) => (
                        <>
                          <div className="smaller">{e.actor_name ?? e.actor_label}</div>
                          {e.on_behalf_of_name ? (
                            <div className="smaller muted">on behalf of {e.on_behalf_of_name}</div>
                          ) : null}
                        </>
                      ),
                      sortValue: (e) => e.actor_name ?? e.actor_label,
                    },
                    {
                      key: 'subject',
                      header: 'About',
                      render: (e) => {
                        const table = e.subject_table;
                        const label = table ? subjectLabels[table] ?? statusLabel(table) : null;
                        if (!e.subject_reference) {
                          return <span className="muted smaller">{label ?? '—'}</span>;
                        }
                        const route = table ? RECORD_ROUTES[table] : undefined;
                        const reference = <span className="mono smaller">{e.subject_reference}</span>;
                        return (
                          <>
                            {route && e.subject_id && canAny(...route.permissions) ? (
                              <Link to={`${route.path}/${e.subject_id}`}>{reference}</Link>
                            ) : (
                              reference
                            )}
                            {label ? <div className="smaller muted">{label}</div> : null}
                          </>
                        );
                      },
                      sortValue: (e) => e.subject_reference ?? '',
                    },
                    {
                      key: 'module',
                      header: 'Module',
                      render: (e) => <span className="smaller">{e.module}</span>,
                      sortValue: (e) => e.module,
                    },
                    {
                      key: 'severity',
                      header: 'Severity',
                      width: '7rem',
                      render: (e) => (
                        <Badge tone={SEVERITY_TONES[e.severity] ?? 'neutral'}>{statusLabel(e.severity)}</Badge>
                      ),
                      sortValue: (e) => e.severity,
                    },
                  ]}
                />
              </Card>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}
